import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import { trackAnalysisCreationPreviewPlay } from '../../../../analytics';

import { SellingPoint } from '../../../../components/custom';
import { Button, Alert } from '../../../../components/general';

import useSampleProjectData from '../../../../hooks/useSampleProjectData';

import { useProjectStore } from '../../../../stores/project';

import { useVideoBuilderStore } from '../../../../stores/videoBuilder';
import {
  Person,
  HlReel,
  FocusedClip,
  HlReelSchemaBlock,
  CreationPreviewClip,
} from '../../../../types';
import { isEmpty, getFileUrl } from '../../../../utils';

import CreationPreview from './creationPreview';
import {
  getHlReelPreviewData,
  getCreationPreviewClips,
} from './getPreviewData';
import HlReelPreviewBlock from './hlReelPreviewBlock';

interface Props {
  hlReel: HlReel | null;
  person?: Person;
  focusedClip?: FocusedClip | null;
  handleAssignFocusedClip?: (shotIndex: number) => void;
}

const HlReelPreview: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const { hlReel, person, focusedClip, handleAssignFocusedClip } = props;

  const { clipSelections } = useVideoBuilderStore();
  const { currentProject } = useProjectStore();

  const [playPreview, setPlayPreview] = useState(false);

  const { isSample } = useSampleProjectData(eventId);

  const isProcessing = !hlReel?.shots;
  const isPortrait = currentProject?.videoMode === 'portrait';

  // array with 5 empty blocks – shows while highlight reel is processing
  let hlReelSchemaEmpty: {}[] = new Array(5).fill({});

  const { hlReelSchemaBlocks, previewClips, previewError } = useMemo(() => {
    let hlReelSchemaBlocks: HlReelSchemaBlock[] = [];
    let previewClips: CreationPreviewClip[] = [];
    let previewError: string | null = null;

    if (!isProcessing) {
      const clips = (person?.clips ?? []).concat(person?.lowScoreClips ?? []);
      const clipSelection = clipSelections.find(
        (selection) => selection.faceId === person?.face.elementId,
      );

      hlReelSchemaBlocks = getHlReelPreviewData({
        hlReel,
        clipSelection,
        clips,
      });

      if (clipSelection)
        previewClips = getCreationPreviewClips({
          hlReel,
          clipSelection,
          selectedClips: clips,
        });

      const allClipsFilled = clipSelection?.wildcardsFiller.every(
        (w) => !!w.clipId,
      );

      if (!allClipsFilled) {
        previewError =
          'Fill all wildcard spots first so we can load the preview.';
        setPlayPreview(false);
      }
    }

    return { hlReelSchemaBlocks, previewClips, previewError };
  }, [isProcessing, hlReel, person, clipSelections]);

  // determine the max number of blocks that fit on the page
  let maxNum;
  if (isPortrait) maxNum = window.innerWidth < 500 ? 3 : !!person ? 7 : 9;
  else maxNum = window.innerWidth < 500 ? 2 : 5;

  const isFitting =
    (isEmpty(hlReelSchemaBlocks) ? hlReelSchemaEmpty : hlReelSchemaBlocks)
      .length <= maxNum;

  const creationPreviewContainer = (
    <React.Fragment>
      {previewError ? (
        <Alert alertData={{ variant: 'warning', text: previewError }} />
      ) : (
        <SellingPoint
          text={
            !playPreview ? (
              <>
                Want a sneak peek of the final video?{' '}
                <Button
                  onClick={() => {
                    setPlayPreview(true);
                    trackAnalysisCreationPreviewPlay(isSample);
                  }}
                  variant="link"
                  colour="white"
                >
                  Play the preview!
                </Button>
              </>
            ) : (
              <>
                This is a preview of the video — the final version will be fully
                polished.{' '}
                <Button
                  onClick={() => setPlayPreview(false)}
                  variant="link"
                  colour="white"
                >
                  Close the preview
                </Button>
              </>
            )
          }
        />
      )}

      {playPreview && (
        <CreationPreview
          hlReelSrc={getFileUrl(
            hlReel?.parentElementId ?? '',
            hlReel?.name ?? '',
            'mp4',
          )}
          previewClips={previewClips}
          className={classNames(
            'self-center',
            isPortrait ? 'sm:w-64 aspect-[9/16]' : 'sm:w-[40vw] aspect-video',
          )}
          isSample={isSample}
        />
      )}
    </React.Fragment>
  );

  return (
    <React.Fragment>
      {person && creationPreviewContainer}

      <div
        className="relative w-full rounded-md border border-grey-200 border-solid"
        id={person ? 'hl-reel-preview-person' : undefined}
      >
        <div
          className={classNames(
            'group flex items-center p-4 gap-4 overflow-x-auto',
            { 'justify-center': isFitting },
          )}
        >
          {isProcessing
            ? hlReelSchemaEmpty.map((_block, index) => (
                <HlReelPreviewBlock
                  key={index}
                  block={null}
                  isPortrait={isPortrait}
                />
              ))
            : hlReelSchemaBlocks.map((block, index) => (
                <HlReelPreviewBlock
                  key={index}
                  block={block}
                  isPortrait={isPortrait}
                  isClipSelection={!!person}
                  focusedClip={focusedClip}
                  onWildcardClick={() =>
                    block.type === 'wildcard' &&
                    handleAssignFocusedClip &&
                    handleAssignFocusedClip(block.shotIndex)
                  }
                />
              ))}
        </div>

        {!isFitting && (
          <div className="absolute top-0 left-0 w-full h-full bg-white-edges rounded-md pointer-events-none z-10" />
        )}
      </div>
    </React.Fragment>
  );
};

export default HlReelPreview;
